import { useEffect, useState } from "react";
import api, { formatApiError, KLIMA_OPTIONS } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShoppingCart, FileDown, Plus, Send, Pencil, Ban, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { buildOrderPDF, buildOrdersListPDF } from "@/lib/pdf";
import { usePdfViewer } from "@/lib/PdfViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";

// Sipariş durumu, ham `status` alanından değil, türetilmiş bir kümeden gösterilir.
// Öncelik: cancelled > has_warranty (Garantide) > revised (Revize edildi/Aktif) > active (Aktif)
const STATUS_META = {
    active: { label: "Aktif", cls: "border-emerald-300 bg-emerald-50 text-emerald-700", icon: null },
    revised_active: { label: "Revize edildi/Aktif", cls: "border-amber-300 bg-amber-50 text-amber-700", icon: RotateCcw },
    warranty: { label: "Garantide", cls: "border-sky-300 bg-sky-50 text-sky-700", icon: ShieldCheck },
    cancelled: { label: "İptal", cls: "border-red-300 bg-red-50 text-red-700 line-through-mark", icon: Ban },
};

const deriveOrderStatus = (o) => {
    if (!o) return "active";
    if (o.status === "cancelled") return "cancelled";
    if (o.has_warranty) return "warranty";
    if (o.status === "revised" || (o.revision_count && o.revision_count > 0)) return "revised_active";
    return "active";
};

const StatusBadge = ({ order, status: rawStatus }) => {
    const key = order ? deriveOrderStatus(order) : (rawStatus || "active");
    const meta = STATUS_META[key] || STATUS_META.active;
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] uppercase tracking-wider font-mono border ${meta.cls}`} data-testid={`order-status-${key}`}>
            {Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />}
            {meta.label}
        </span>
    );
};

const OrderForm = ({ onCreated }) => {
    const [klima, setKlima] = useState("");
    const [ilaveAku, setIlaveAku] = useState("Hayır");
    const [adet, setAdet] = useState(1);
    const [notlar, setNotlar] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setKlima(""); setIlaveAku("Hayır"); setAdet(1); setNotlar("");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!klima) {
            toast.error("Lütfen klima modeli seçin");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/orders", {
                klima_modeli: klima,
                ilave_aku: ilaveAku,
                adet: Number(adet) || 1,
                notlar,
            });
            toast.success(`Sipariş oluşturuldu: ${data.order_no}`);
            reset();
            onCreated?.(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-zinc-200 bg-white" data-testid="order-form">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                    <div className="tag-label mb-1">Modül / Sipariş</div>
                    <h2 className="font-display text-xl font-bold tracking-tight">Sipariş Girişi</h2>
                </div>
                <Plus className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
            </div>

            <div className="p-6 md:p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label className="tag-label" htmlFor="order-klima">Klima Seçiniz</Label>
                        <Select value={klima} onValueChange={setKlima}>
                            <SelectTrigger id="order-klima" className="rounded-none h-12 border-zinc-300 focus:ring-0 focus:border-black" data-testid="order-klima-select">
                                <SelectValue placeholder="Model seçin..." />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                {KLIMA_OPTIONS.map((k) => (
                                    <SelectItem key={k} value={k} className="rounded-none font-mono">{k}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label className="tag-label" htmlFor="order-adet">Adet</Label>
                        <Input
                            id="order-adet"
                            type="number"
                            min="1"
                            value={adet}
                            onChange={(e) => setAdet(e.target.value)}
                            className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                            data-testid="order-adet-input"
                        />
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="tag-label">İlave Akü</Label>
                    <RadioGroup value={ilaveAku} onValueChange={setIlaveAku} className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200" data-testid="order-ilave-aku-group">
                        {["Evet", "Hayır"].map((opt) => (
                            <label
                                key={opt}
                                className={`bg-white px-6 py-4 cursor-pointer transition-colors flex items-center gap-3 ${ilaveAku === opt ? "bg-black text-white" : "hover:bg-zinc-50"}`}
                                style={ilaveAku === opt ? { backgroundColor: "#0A0A0A", color: "#FFF" } : {}}
                                data-testid={`order-ilave-aku-${opt.toLowerCase()}`}
                            >
                                <RadioGroupItem value={opt} className="border-current" />
                                <span className="font-semibold">{opt}</span>
                            </label>
                        ))}
                    </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label className="tag-label" htmlFor="order-notlar">Notlar</Label>
                        <Textarea id="order-notlar" rows={3} value={notlar} onChange={(e) => setNotlar(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="order-notlar-input" />
                    </div>
                </div>
            </div>

            <div className="px-6 md:px-8 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end">
                <Button type="submit" disabled={submitting} className="h-12 px-8 rounded-none bg-black hover:bg-zinc-800 text-white font-semibold" data-testid="order-submit-button">
                    <Send className="w-4 h-4 mr-2" strokeWidth={2} />
                    {submitting ? "Gönderiliyor..." : "SİPARİŞİ KAYDET"}
                </Button>
            </div>
        </form>
    );
};

const OrdersList = ({ orders, refresh }) => {
    const { openPdf } = usePdfViewer();
    const { user } = useAuth();
    const [editTarget, setEditTarget] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);

    const canModify = (o) =>
        o.status !== "cancelled" &&
        user?.role !== "uretici" &&
        (user?.role === "admin" || o.bayi_id === user?.id);

    return (
        <div className="border border-zinc-200 bg-white" data-testid="orders-list">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="tag-label mb-1">Modül / Sipariş</div>
                    <h2 className="font-display text-xl font-bold tracking-tight">Sipariş Görüntüle ({orders.length})</h2>
                </div>
                <div className="flex gap-2">
                    <Button onClick={refresh} variant="outline" className="rounded-none h-10 border-zinc-300" data-testid="orders-refresh-button">
                        Yenile
                    </Button>
                    <Button
                        onClick={async () => {
                            const { blob, filename } = await buildOrdersListPDF(orders);
                            openPdf(blob, filename);
                        }}
                        disabled={orders.length === 0}
                        className="rounded-none h-10 bg-black hover:bg-zinc-800 text-white"
                        data-testid="orders-download-list-pdf"
                    >
                        <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Listeyi PDF İndir
                    </Button>
                </div>
            </div>

            {orders.length === 0 ? (
                <div className="p-16 text-center">
                    <ShoppingCart className="w-10 h-10 mx-auto text-zinc-300 mb-3" strokeWidth={1} />
                    <div className="text-sm text-zinc-500">Henüz sipariş kaydı bulunmuyor.</div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-200 hover:bg-transparent">
                                <TableHead className="tag-label h-11">Sipariş No</TableHead>
                                <TableHead className="tag-label">Durum</TableHead>
                                <TableHead className="tag-label">Tarih</TableHead>
                                <TableHead className="tag-label">Oluşturan</TableHead>
                                <TableHead className="tag-label">Klima</TableHead>
                                <TableHead className="tag-label">İlave Akü</TableHead>
                                <TableHead className="tag-label">Adet</TableHead>
                                <TableHead className="tag-label">Notlar</TableHead>
                                <TableHead className="tag-label text-right">Aksiyon</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orders.map((o) => {
                                const status = o.status || "active";
                                const muted = status === "cancelled";
                                return (
                                <TableRow key={o.id} className={`border-zinc-200 ${muted ? "opacity-60" : ""}`} data-testid={`order-row-${o.id}`}>
                                    <TableCell className={`font-mono text-xs ${muted ? "line-through" : ""}`}>{o.order_no}</TableCell>
                                    <TableCell><StatusBadge order={o} /></TableCell>
                                    <TableCell className="text-sm">{new Date(o.created_at).toLocaleString("tr-TR")}</TableCell>
                                    <TableCell className="text-sm font-medium">{o.bayi_name}</TableCell>
                                    <TableCell className="font-mono text-xs">{o.klima_modeli}</TableCell>
                                    <TableCell className="text-sm">{o.ilave_aku}</TableCell>
                                    <TableCell className="text-sm">{o.adet}</TableCell>
                                    <TableCell className="text-sm max-w-[260px] truncate" title={o.notlar || (status==='cancelled' ? `İptal sebebi: ${o.cancel_reason||'-'}` : '')}>
                                        {status === "cancelled" && o.cancel_reason ? <span className="text-red-600">İptal: {o.cancel_reason}</span> : (o.notlar || "-")}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                                        {canModify(o) && (
                                            <>
                                                <Button onClick={() => setEditTarget(o)} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300" data-testid={`order-edit-${o.id}`} title="Revize et">
                                                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                </Button>
                                                <Button onClick={() => setCancelTarget(o)} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300" data-testid={`order-cancel-${o.id}`} title="İptal et">
                                                    <Ban className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                </Button>
                                            </>
                                        )}
                                        <Button
                                            onClick={async () => {
                                                const { blob, filename } = await buildOrderPDF(o);
                                                openPdf(blob, filename);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="rounded-none border-zinc-300 hover:bg-zinc-100"
                                            data-testid={`order-pdf-${o.id}`}
                                        >
                                            <FileDown className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                                            PDF
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );})}
                        </TableBody>
                    </Table>
                </div>
            )}

            <EditOrderDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); refresh(); }} />
            <CancelOrderDialog target={cancelTarget} onClose={() => setCancelTarget(null)} onCancelled={() => { setCancelTarget(null); refresh(); }} />
        </div>
    );
};

const EditOrderDialog = ({ target, onClose, onSaved }) => {
    const [klima, setKlima] = useState("");
    const [ilaveAku, setIlaveAku] = useState("Hayır");
    const [adet, setAdet] = useState(1);
    const [notlar, setNotlar] = useState("");
    const [revisionNote, setRevisionNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (target) {
            setKlima(target.klima_modeli);
            setIlaveAku(target.ilave_aku);
            setAdet(target.adet);
            setNotlar(target.notlar || "");
            setRevisionNote("");
        }
    }, [target]);

    if (!target) return null;

    const submit = async () => {
        setBusy(true);
        try {
            await api.patch(`/orders/${target.id}`, {
                klima_modeli: klima,
                ilave_aku: ilaveAku,
                adet: Number(adet) || 1,
                notlar,
                revision_note: revisionNote,
            });
            toast.success("Sipariş revize edildi");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg rounded-none border-zinc-200">
                <DialogHeader>
                    <DialogTitle className="font-display tracking-tight">Sipariş Revize — <span className="font-mono text-base">{target.order_no}</span></DialogTitle>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    <div className="space-y-2">
                        <Label className="tag-label">Klima Seçiniz</Label>
                        <Select value={klima} onValueChange={setKlima}>
                            <SelectTrigger className="rounded-none h-11 border-zinc-300"><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-none">
                                {KLIMA_OPTIONS.map((k) => <SelectItem key={k} value={k} className="rounded-none font-mono">{k}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="tag-label">İlave Akü</Label>
                            <Select value={ilaveAku} onValueChange={setIlaveAku}>
                                <SelectTrigger className="rounded-none h-11 border-zinc-300"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="Evet" className="rounded-none">Evet</SelectItem>
                                    <SelectItem value="Hayır" className="rounded-none">Hayır</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label">Adet</Label>
                            <Input type="number" min="1" value={adet} onChange={(e) => setAdet(e.target.value)} className="rounded-none h-11 border-zinc-300" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="tag-label">Notlar</Label>
                        <Textarea rows={2} value={notlar} onChange={(e) => setNotlar(e.target.value)} className="rounded-none border-zinc-300" />
                    </div>
                    <div className="space-y-2">
                        <Label className="tag-label">Revizyon Açıklaması</Label>
                        <Textarea rows={2} value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} placeholder="Bu revizyonun sebebini kısaca yazın (opsiyonel)" className="rounded-none border-zinc-300" data-testid="order-revision-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none">Vazgeç</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-black hover:bg-zinc-800 text-white" data-testid="order-revision-submit">
                        {busy ? "Güncelleniyor..." : "Revizyonu Kaydet"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const CancelOrderDialog = ({ target, onClose, onCancelled }) => {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (target) setReason(""); }, [target]);

    if (!target) return null;

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/orders/${target.id}/cancel`, { cancel_reason: reason });
            toast.success("Sipariş iptal edildi");
            onCancelled?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <AlertDialogContent className="rounded-none border-zinc-200">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-display tracking-tight">Siparişi iptal et?</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3">
                        <span className="block">
                            <strong className="font-mono">{target.order_no}</strong> numaralı sipariş iptal edilecek. Liste'de gösterilmeye devam eder, ancak yeni garanti girişi açılamaz. Admin ve bayinin e-posta adresine bildirim gider.
                        </span>
                        <Textarea
                            rows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="İptal sebebi (opsiyonel)"
                            className="rounded-none border-zinc-300 mt-2"
                            data-testid="order-cancel-reason"
                        />
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none" disabled={busy}>Vazgeç</AlertDialogCancel>
                    <AlertDialogAction onClick={submit} disabled={busy} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="order-cancel-confirm">
                        {busy ? "İptal ediliyor..." : "Evet, İptal Et"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

const OrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [tab, setTab] = useState("giris");

    const load = async () => {
        try {
            const { data } = await api.get("/orders");
            setOrders(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] animate-fade-up" data-testid="orders-page">
            <div className="mb-10">
                <div className="tag-label mb-3">Modül</div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-2">
                    Sipariş.
                </h1>
                <p className="text-zinc-600 max-w-xl">Yeni sipariş oluşturun veya geçmiş kayıtları görüntüleyin.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-none bg-transparent p-0 border-b border-zinc-200 mb-8 h-auto">
                    <TabsTrigger
                        value="giris"
                        data-testid="orders-tab-giris"
                        className="rounded-none px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-none border-x border-t border-transparent data-[state=active]:border-black font-semibold"
                    >
                        Sipariş Girişi
                    </TabsTrigger>
                    <TabsTrigger
                        value="goruntule"
                        data-testid="orders-tab-goruntule"
                        className="rounded-none px-6 py-3 data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-none border-x border-t border-transparent data-[state=active]:border-black font-semibold"
                    >
                        Sipariş Görüntüle
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="giris" className="mt-0">
                    <OrderForm onCreated={() => { load(); setTab("goruntule"); }} />
                </TabsContent>
                <TabsContent value="goruntule" className="mt-0">
                    <OrdersList orders={orders} refresh={load} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default OrdersPage;
